<template>
  <DsfrCallout v-if="hasProposal || provenance.length">
    <div>
      <h3 class="fr-text--sm mb-1">Reprendre mes pesées Labricot</h3>
      <template v-if="hasProposal">
        <p class="mb-2">
          {{ summary }}. Ces valeurs seront recopiées dans le formulaire ci-dessous&nbsp;: vous pourrez les corriger
          avant d'enregistrer.
        </p>
        <ul class="mb-2 fr-text--sm">
          <li v-for="field in prefill.fields" :key="field.name">
            <b>{{ labelFor(field.name) }}</b>
            &nbsp;: {{ formatValue(field) }} — {{ field.origin }}
          </li>
        </ul>
        <p v-if="unavailableLabels" class="mb-2 fr-text--sm">
          Restent à saisir&nbsp;: {{ unavailableLabels }}. Labricot ne collecte pas cette information.
        </p>
      </template>
      <!-- Provenance déjà enregistrée : ce qui vient des pesées, ce qui a été corrigé depuis.
           L'état est écrit en toutes lettres, jamais porté par la seule couleur (RGAA). -->
      <div v-if="provenance.length" class="mb-2">
        <h4 class="fr-text--sm mb-1">Provenance de vos réponses</h4>
        <ul class="fr-text--sm">
          <li v-for="field in provenance" :key="field.fieldName">
            <b>{{ labelFor(field.fieldName) }}</b>
            &nbsp;: {{ provenanceLabel(field) }}
            <span v-if="field.sourceMeasurements.length">
              — {{ field.sourceMeasurements.length }} pesée{{
                field.sourceMeasurements.length > 1 ? "s" : ""
              }}
              d'origine ({{ measurementsSummary(field) }})
            </span>
          </li>
        </ul>
      </div>
      <!-- Le formulaire amont attend des kg/an pour le détail des restes, alors que la
           proposition ne reporte que ce qui a été réellement pesé, sans extrapolation. -->
      <p v-if="hasProposal && partialPeriod" class="mb-2 fr-text--sm">
        Le champ «&nbsp;reste de pain&nbsp;» du formulaire s'entend en kg/an&nbsp;: la valeur proposée est celle pesée
        sur la période ci-dessus, à ajuster si votre mesure ne couvre pas l'année entière.
      </p>
      <v-btn v-if="hasProposal" outlined color="primary" :disabled="applied" @click="apply">
        {{ applied ? "Valeurs reportées" : "Reprendre mes pesées" }}
      </v-btn>
    </div>
  </DsfrCallout>
</template>

<script>
import DsfrCallout from "@/components/DsfrCallout"
import { formatDate } from "@/utils"

// Libellés des champs du bilan, repris de l'étape « mesure du gaspillage » pour que
// la proposition nomme les champs comme le formulaire les nomme.
const FIELD_LABELS = {
  hasWasteMeasures: "J'ai réalisé des mesures de mon gaspillage alimentaire",
  totalLeftovers: "Total des déchets alimentaires",
  durationLeftoversMeasurement: "Période de mesure",
  breadLeftovers: "Reste de pain",
  servedLeftovers: "Reste plateau",
  unservedLeftovers: "Reste en production (non servi)",
  sideLeftovers: "Reste de composantes (entrée, plat, dessert…)",
}

const FIELD_UNITS = {
  totalLeftovers: "kg",
  breadLeftovers: "kg",
  servedLeftovers: "kg",
  unservedLeftovers: "kg",
  sideLeftovers: "kg",
  durationLeftoversMeasurement: "jours",
}

export default {
  name: "LabricotWastePrefillOption",
  props: {
    canteenId: {
      required: true,
    },
    year: {
      required: true,
    },
  },
  components: {
    DsfrCallout,
  },
  data() {
    return {
      prefill: null,
      applied: false,
      provenance: [],
    }
  },
  computed: {
    hasProposal() {
      // Sans pesée sur l'année, il n'y a rien à proposer — mais une provenance enregistrée
      // les années précédentes reste, elle, digne d'être affichée.
      return Boolean(this.prefill && this.prefill.measurementCount)
    },
    summary() {
      const { measurementCount, measuredDays, periodStart, periodEnd } = this.prefill
      const pesees = `${measurementCount} pesée${measurementCount > 1 ? "s" : ""}`
      const jours = `${measuredDays} jour${measuredDays > 1 ? "s" : ""}`
      const periode =
        periodStart === periodEnd
          ? `le ${formatDate(periodStart)}`
          : `du ${formatDate(periodStart)} au ${formatDate(periodEnd)}`
      return `${pesees} enregistrées sur ${jours}, ${periode}`
    },
    partialPeriod() {
      // Une mesure qui ne couvre pas l'année entière ne peut pas se lire comme un kg/an.
      return this.prefill?.payload?.breadLeftovers !== undefined
    },
    unavailableLabels() {
      const fields = this.prefill?.unavailableFields || []
      return fields.map((field) => this.labelFor(field.name)).join(", ")
    },
  },
  methods: {
    labelFor(name) {
      return FIELD_LABELS[name] || name
    },
    formatValue(field) {
      if (field.value === true) return "oui"
      const unit = FIELD_UNITS[field.name]
      return unit ? `${field.value} ${unit}` : `${field.value}`
    },
    provenanceLabel(field) {
      if (field.origin === "MANUAL") return "corrigé à la main, votre valeur est conservée"
      return `déduit de vos pesées — ${field.sourceLabel || "collecte Labricot"}`
    },
    measurementsSummary(field) {
      // Les pesées se désignent par leur date et leur masse, jamais par leur auteur (RGPD).
      return field.sourceMeasurements.map((m) => `${formatDate(m.mealDate)} — ${m.totalWeightKg} kg`).join(", ")
    },
    loadProvenance() {
      return (
        this.$store
          .dispatch("fetchDiagnosticProvenance", { canteenId: this.canteenId, year: this.year })
          .then((body) => (this.provenance = body.fields || []))
          // Bilan pas encore créé, ou endpoint indisponible : il n'y a simplement rien à
          // afficher. Première année sans collecte, c'est le cas nominal.
          .catch(() => (this.provenance = []))
      )
    },
    apply() {
      this.applied = true
      // Contrat identique à `LastYearAutofillOption` : un payload partiel, que l'étape
      // fusionne dans le formulaire. Aucune sauvegarde n'est déclenchée ici.
      this.$emit("labricot-prefill", {
        payload: { ...this.prefill.payload },
        message: {
          status: "success",
          message: "Vos pesées ont été reportées dans le formulaire. Vérifiez-les avant d'enregistrer.",
        },
      })
      // Le report appliqué est tracé : c'est ce geste qui écrit, pas la lecture de la
      // proposition. Un échec ne doit pas empêcher de remplir le bilan — la trace manquera,
      // le formulaire non.
      this.$store
        .dispatch("recordDiagnosticProvenance", {
          canteenId: this.canteenId,
          year: this.year,
          fields: this.prefill.fields,
          measurementIds: this.prefill.measurementIds,
        })
        .then((body) => (this.provenance = body.fields || []))
        .catch(() => {})
    },
  },
  mounted() {
    if (!this.canteenId || !this.year) return
    this.$store
      .dispatch("fetchTeledeclarationPrefill", { canteenId: this.canteenId, year: this.year })
      // Absence de pesées ou endpoint indisponible : la proposition ne s'affiche pas,
      // le formulaire reste utilisable tel quel. Rien à signaler à l'utilisateur.
      .then((prefill) => (this.prefill = prefill))
      .catch(() => (this.prefill = null))
    this.loadProvenance()
  },
}
</script>
